import React from 'react';
import { getAssetColorPalette } from '../../utils/assetHelpers';

interface CryptoCarProps {
  symbol: string;
  isVertical?: boolean; // For vertical racing layout
  showExhaustStreaks?: boolean; // Show hyperspeed streaks during racing
  isRacing?: boolean; // Enable racing drift animation
  driftPattern?: 1 | 2; // Different drift timing patterns for variety
  laneIndex?: number; // Lane position (0 = left, 1 = right) for horizontal perspective skew
}

const CryptoCar: React.FC<CryptoCarProps> = ({ 
  symbol, 
  isVertical = false, 
  showExhaustStreaks = false,
  isRacing = false,
  driftPattern,
  laneIndex = 0
}) => {
  // Get color palette from centralized asset helpers (single source of truth)
  const palette = getAssetColorPalette(symbol);
  
  // Generate accent color (lighter than secondary for highlights)
  const lightenColor = (hex: string): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    const lighten = (val: number) => Math.min(255, Math.floor(val + (255 - val) * 0.3));
    
    return `#${lighten(r).toString(16).padStart(2, '0')}${lighten(g).toString(16).padStart(2, '0')}${lighten(b).toString(16).padStart(2, '0')}`;
  };
  
  const colors = {
    primary: palette.primary,
    secondary: palette.secondary,
    accent: lightenColor(palette.secondary),
    dark: palette.dark
  };

  // Build className for racing drift
  const containerClasses = [
    'crypto-car-container',
    'svg-car',
    isRacing && 'racing-drift',
    isRacing && driftPattern && `drift-pattern-${driftPattern}`,
    isRacing && `lane-${laneIndex}` // Add lane-specific class for horizontal perspective
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {/* SVG Rocket - Dynamic Colors Based on Crypto */}
      <svg width="81" height="114" viewBox="0 0 81 114" fill="none" xmlns="http://www.w3.org/2000/svg" className="rocket-svg">
        <defs>
          {/* Gradients for rocket body */}
          <linearGradient id={`rocket_body_${symbol}`} x1="40.5938" y1="8" x2="40.5938" y2="104" gradientUnits="userSpaceOnUse">
            <stop stopColor={colors.primary}/>
            <stop offset="0.5" stopColor={colors.secondary}/>
            <stop offset="1" stopColor={colors.dark}/>
          </linearGradient>
          <radialGradient id={`rocket_window_${symbol}`} cx="0.5" cy="0.5" r="0.5">
            <stop stopColor={colors.accent}/>
            <stop offset="1" stopColor={colors.primary}/>
          </radialGradient>
        </defs>
        
        {/* Left Fin */}
        <path d="M79.0938 88.5L57.8121 61L49.0938 90L62.0982 96.5V113H72.5307L79.0938 88.5Z" 
          fill={colors.dark} 
          fillOpacity="0.3" 
          stroke={colors.primary} 
          strokeWidth="2"/>
        
        {/* Right Fin */}
        <path d="M1.09375 88.5L22.3754 61L31.0938 90L18.0893 96.5V113H7.6568L1.09375 88.5Z" 
          fill={colors.dark} 
          fillOpacity="0.3" 
          stroke={colors.primary} 
          strokeWidth="2"/>
        
        {/* Main Rocket Body */}
        <path d="M34.6729 8.00391C37.8471 4.51035 43.3404 4.51035 46.5146 8.00391L53.502 15.6943C63.2092 26.3787 67.0584 41.1455 63.8018 55.209L52.8613 102.451C52.6513 103.358 51.8438 104 50.9131 104H30.2744C29.3437 104 28.5362 103.358 28.3262 102.451L17.3857 55.209C14.1291 41.1455 17.9783 26.3787 27.6855 15.6943L34.6729 8.00391Z" 
          fill={`url(#rocket_body_${symbol})`} 
          stroke={colors.primary} 
          strokeWidth="2"/>
        
        {/* Window */}
        <circle cx="40.7969" cy="46" r="11" 
          fill={colors.dark} 
          fillOpacity="1" 
          stroke={colors.primary} 
          strokeWidth="2"/>
        
        {/* Bottom Detail Line */}
        <path d="M28.0938 98L54.0937 98" stroke={colors.primary} strokeWidth="2"/>
        
        {/* Top Detail Line */}
        <path d="M23.0938 22H57.5938" stroke={colors.primary} strokeWidth="2"/>
      </svg>
      
      {/* Rocket Fuel Exhaust - Takeoff Style */}
      {showExhaustStreaks && (
        <div className="rocket-exhaust">
          {/* Main thrust flames */}
          <div className="thrust-flame flame-center" style={{ '--flame-color': colors.primary } as React.CSSProperties}>
            <div className="flame-core"></div>
            <div className="flame-outer"></div>
          </div>
          <div className="thrust-flame flame-left" style={{ '--flame-color': colors.secondary } as React.CSSProperties}>
            <div className="flame-core"></div>
            <div className="flame-outer"></div>
          </div>
          <div className="thrust-flame flame-right" style={{ '--flame-color': colors.secondary } as React.CSSProperties}>
            <div className="flame-core"></div>
            <div className="flame-outer"></div>
          </div>
          
          {/* Fuel particles */}
          <div className="fuel-particles">
            <div className="fuel-particle particle-1" style={{ '--particle-color': colors.primary } as React.CSSProperties}></div>
            <div className="fuel-particle particle-2" style={{ '--particle-color': colors.accent } as React.CSSProperties}></div>
            <div className="fuel-particle particle-3" style={{ '--particle-color': colors.secondary } as React.CSSProperties}></div>
            <div className="fuel-particle particle-4" style={{ '--particle-color': colors.primary } as React.CSSProperties}></div>
            <div className="fuel-particle particle-5" style={{ '--particle-color': colors.accent } as React.CSSProperties}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoCar;

